import React, { useState, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card } from '../ui/card';
import { toast } from 'sonner';
import { Plus, X, Image, Loader2 } from 'lucide-react';
import { validateUrl } from '../../utils/socialPlatforms';

/* ─── Custom Link Add Form ─── */
export const CustomLinkForm = ({ onAdd }) => {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [ctaText, setCtaText] = useState('Click Me!');
  const [ctaColor, setCtaColor] = useState('#FFD500');
  const [imageFile, setImageFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const imgRef = useRef(null);
  const imgPreview = imageFile ? URL.createObjectURL(imageFile) : null;

  const handleAdd = async () => {
    if (!url.trim()) { toast.error('Please enter a URL'); return; }
    if (!validateUrl(url.trim())) { toast.error('Please enter a valid URL'); return; }
    setSaving(true);
    try {
      await onAdd({
        url: url.trim(),
        title: title.trim() || 'Custom Link',
        description: description.trim() || undefined,
        cta_text: ctaText.trim() || undefined,
        cta_color: ctaColor || undefined,
        image_file: imageFile,
      });
      setUrl(''); setTitle(''); setDescription(''); setCtaText('Click Me!'); setCtaColor('#FFD500'); setImageFile(null);
      if (imgRef.current) imgRef.current.value = '';
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} className="w-full mt-3" data-testid="add-custom-link-btn">
        <Plus className="w-4 h-4 mr-2" /> Add Custom Link
      </Button>
    );
  }

  return (
    <Card className="p-4 mt-3 bg-muted/30 border-dashed" data-testid="custom-link-form">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold">Custom Link Card</span>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setOpen(false); }}>
          <X className="w-4 h-4" />
        </Button>
      </div>
      <div className="space-y-3">
        <div>
          <Label className="text-xs">Destination URL *</Label>
          <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://any-link.com" className="h-9 text-sm mt-1" data-testid="custom-link-url" />
        </div>
        <div>
          <Label className="text-xs">Title *</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Pre-Order What's Your Story?" className="h-9 text-sm mt-1" data-testid="custom-link-title" maxLength={80} />
        </div>
        <div>
          <Label className="text-xs">Description <span className="text-muted-foreground">(optional, max 300)</span></Label>
          <Textarea value={description} onChange={(e) => setDescription(e.target.value.slice(0, 300))} placeholder="Short pitch shown under the title…" className="text-sm mt-1 min-h-[60px]" data-testid="custom-link-description" />
        </div>

        {/* Image upload */}
        <div>
          <Label className="text-xs">Thumbnail image <span className="text-muted-foreground">(optional)</span></Label>
          <div className="flex items-center gap-3 mt-1">
            {imgPreview ? (
              <img src={imgPreview} alt="" className="w-14 h-14 rounded-lg object-cover" />
            ) : (
              <div className="w-14 h-14 rounded-lg bg-muted flex items-center justify-center text-muted-foreground">
                <Image className="w-5 h-5" />
              </div>
            )}
            <Button variant="outline" size="sm" type="button" onClick={() => imgRef.current?.click()} data-testid="custom-link-image-btn">
              <Image className="w-4 h-4 mr-2" /> {imageFile ? 'Change' : 'Upload'}
            </Button>
            {imageFile && (
              <Button variant="ghost" size="sm" type="button" onClick={() => { setImageFile(null); if (imgRef.current) imgRef.current.value = ''; }}>
                <X className="w-3 h-3 mr-1" /> Remove
              </Button>
            )}
            <input ref={imgRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" className="hidden"
              onChange={(e) => { if (e.target.files[0]) setImageFile(e.target.files[0]); }} />
          </div>
        </div>

        {/* CTA button */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Button text</Label>
            <Input value={ctaText} onChange={(e) => setCtaText(e.target.value.slice(0, 40))} placeholder="Click Me!" className="h-9 text-sm mt-1" data-testid="custom-link-cta-text" />
          </div>
          <div>
            <Label className="text-xs">Button color</Label>
            <div className="flex items-center gap-2 mt-1">
              <input type="color" value={ctaColor} onChange={(e) => setCtaColor(e.target.value)} className="h-9 w-12 rounded cursor-pointer border" data-testid="custom-link-cta-color" />
              <Input value={ctaColor} onChange={(e) => setCtaColor(e.target.value)} className="h-9 text-sm font-mono flex-1" />
            </div>
          </div>
        </div>

        <Button onClick={handleAdd} disabled={saving} size="sm" className="w-full" data-testid="custom-link-submit">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />} Add Link Card
        </Button>
      </div>
    </Card>
  );
};
